import { useState } from "react";
import { Upload, Tags, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  extractDroppedFiles,
  isEmptySelection,
  type UploadSelection,
} from "../utils/uploadSelection";

interface DocumentsEmptyStateProps {
  onUploadClick?: () => void;
  onFileDrop?: (selection: UploadSelection) => void;
}

export function DocumentsEmptyState({ onUploadClick, onFileDrop }: DocumentsEmptyStateProps) {
  const { t } = useLanguage();
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    if (!onFileDrop) return;
    // Traverse dropped folders like every other drop zone: a flat
    // `dataTransfer.files` loses their contents and structure.
    extractDroppedFiles(e.dataTransfer)
      .then((selection) => {
        if (!isEmptySelection(selection)) onFileDrop(selection);
      })
      .catch((error) => {
        console.error("Failed to read dropped files:", error);
        toast.error(t.documents.uploadModal.readFailed);
      });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  return (
    <div className="space-y-8">
      {/* Upload Area */}
      <Card
        className={`border-2 border-dashed transition-colors ${
          isDragOver
            ? "border-primary bg-primary/5"
            : "border-muted-foreground/25 hover:border-muted-foreground/50"
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <CardContent className="flex flex-col items-center justify-center p-12 text-center">
          <Upload className="mb-4 h-16 w-16 text-muted-foreground" />
          <h3 className="mb-2 text-xl font-semibold">
            {isDragOver ? t.documents.empty.dropHere : t.documents.empty.uploadFirst}
          </h3>
          <p className="mb-6 max-w-md text-muted-foreground">{t.documents.empty.description}</p>
          <Button onClick={onUploadClick} size="lg" className="mb-4">
            <Upload className="mr-2 h-5 w-5" />
            {t.documents.empty.uploadButton}
          </Button>
          <p className="text-sm text-muted-foreground">{t.documents.empty.fileTypes}</p>
        </CardContent>
      </Card>

      {/* Feature Overview */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start space-x-4">
              <div className="rounded-lg bg-primary/10 p-3">
                <Tags className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h4 className="mb-2 font-semibold">{t.documents.empty.autoCategorisation}</h4>
                <p className="text-sm text-muted-foreground">
                  {t.documents.empty.autoCategorisationDesc}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start space-x-4">
              <div className="rounded-lg bg-primary/10 p-3">
                <Clock className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h4 className="mb-2 font-semibold">{t.documents.empty.timelineVersioning}</h4>
                <p className="text-sm text-muted-foreground">
                  {t.documents.empty.timelineVersioningDesc}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
